import React from 'react';

// Enhanced skeleton component with shimmer effect
const SkeletonBase: React.FC<{ className?: string; children?: React.ReactNode }> = ({ 
  className = '', 
  children 
}) => (
  <div className={`relative overflow-hidden bg-gray-200 dark:bg-gray-600/30 rounded ${className}`}>
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 dark:via-gray-500/20 to-transparent"></div>
    {children}
  </div>
);

// Skeleton for text content
const SkeletonText: React.FC<{ 
  width?: string; 
  height?: string; 
  className?: string 
}> = ({ 
  width = 'w-full', 
  height = 'h-5', 
  className = '' 
}) => (
  <SkeletonBase className={`${width} ${height} ${className} bg-gray-200 dark:bg-gray-600/30`} />
);

// Skeleton for color indicator (circular/oval)
const SkeletonColorIndicator: React.FC = () => (
  <SkeletonBase className="w-12 h-6 rounded-full bg-gray-250 dark:bg-gray-600/30" />
);

// Skeleton for action dots (three dots menu)
const SkeletonActionDots: React.FC = () => (
  <div className="flex justify-center">
    <SkeletonBase className="w-6 h-6 rounded-full bg-gray-250 dark:bg-gray-600/30" />
  </div>
);

// Skeleton for currency values
const SkeletonCurrency: React.FC<{ width?: string }> = ({ width = 'w-16' }) => (
  <SkeletonText width={width} height="h-5" className="bg-gray-200 dark:bg-gray-600/30" />
);

// Generic skeleton row for any table
const SkeletonRow: React.FC<{ columns: any[] }> = ({ columns }) => (
  <tr className="bg-white/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700">
    {columns.map((column, index) => (
      <td 
        key={`skeleton-${column.key}-${index}`} 
        className={`px-6 py-4 ${column.width || ''}`}
        style={{
          width: column.width && !column.width.startsWith('w-') ? column.width : undefined,
          minWidth: column.minWidth,
          maxWidth: column.maxWidth
        }}
      >
        <div className="flex items-center">
          {renderSkeletonForColumn(column)}
        </div>
      </td>
    ))}
  </tr>
);

// Function to render appropriate skeleton based on column type
const renderSkeletonForColumn = (column: any) => {
  switch (column.key) {
    // For Skills table
    case 'skill':
      return <SkeletonText width="w-32" height="h-5" />;
    
    case 'description':
      return <SkeletonText width="w-48" height="h-5" />;
    
    case 'cost_per_hour':
    case 'billing_per_hour':
      return <SkeletonCurrency width="w-16" />;
    
    case 'color_code':
      return <SkeletonColorIndicator />;
    
    // For generic tables (like first image)
    case 'name':
      return <SkeletonText width="w-36" height="h-5" />;
    
    case 'date':
      return <SkeletonText width="w-24" height="h-5" />;
    
    case 'type':
      return <SkeletonText width="w-16" height="h-5" />;
    
    case 'action':
      return <SkeletonActionDots />;
    
    // Default case
    default:
      return <SkeletonText width="w-24" height="h-5" />;
  }
};

// Skills table specific skeleton rows with customizable widths
const SkillsSkeletonRows: React.FC<{ 
  rowCount?: number;
  columns?: any[]; // Accept columns to respect custom widths
}> = ({ rowCount = 5, columns }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <tr 
        key={`skills-skeleton-${index}`} 
        className="bg-white/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700"
      >
        {columns ? (
          // Use custom column widths if provided
          columns.map((column, colIndex) => (
            <td 
              key={`skeleton-${column.key}-${colIndex}`}
              className={`px-6 py-4 ${column.width || ''}`}
              style={{
                width: column.width && !column.width.startsWith('w-') ? column.width : undefined,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth
              }}
            >
              <div className="flex items-center">
                {renderSkeletonForColumn(column)}
              </div>
            </td>
          ))
        ) : (
          // Default skills table skeleton
          <>
            {/* Skill Name */}
            <td className="px-6 py-4">
              <div className="flex items-center">
                <SkeletonText width="w-32" height="h-5" />
              </div>
            </td>
            
            {/* Job Duties */}
            <td className="px-6 py-4">
              <div className="flex items-center">
                <SkeletonText width="w-48" height="h-5" />
              </div>
            </td>
            
            {/* Skill Cost */}
            <td className="px-6 py-4">
              <div className="flex items-center">
                <SkeletonCurrency width="w-16" />
              </div>
            </td>
            
            {/* Skill Billing */}
            <td className="px-6 py-4">
              <div className="flex items-center">
                <SkeletonCurrency width="w-16" />
              </div>
            </td>
            
            {/* Skill Color */}
            <td className="px-6 py-4">
              <div className="flex items-center">
                <SkeletonColorIndicator />
              </div>
            </td>
          </>
        )}
      </tr>
    ))}
  </>
);

// Generic table skeleton rows
const GenericSkeletonRows: React.FC<{ 
  columns: any[]; 
  rowCount?: number 
}> = ({ columns, rowCount = 5 }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <SkeletonRow key={`generic-skeleton-${index}`} columns={columns} />
    ))}
  </>
);

// Export all components
export {
  SkeletonBase,
  SkeletonText,
  SkeletonColorIndicator,
  SkeletonActionDots,
  SkeletonCurrency,
  SkeletonRow,
  SkillsSkeletonRows,
  GenericSkeletonRows,
  renderSkeletonForColumn
};